import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import { ArrowRight, Mail, Phone, Calendar, Building2, FileText, Briefcase } from 'lucide-react';
import { Button, subtleNeo } from '@vidnight/ui';
import { localizedName } from '@/utils/localized';
import { useUserDetail } from '../hooks';
import { ADMIN_COLORS } from '../../admin/utils/adminAnimations';

const ROLE_COLORS: Record<string, string> = {
  SUPER_ADMIN: 'bg-slate-800 text-white',
  ADMIN: 'bg-primary-500 text-white',
  MANAGER: 'bg-slate-50 text-slate-800',
  THERAPIST: 'bg-slate-50 text-slate-800',
};

export function UserDetailPage() {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: user, isLoading } = useUserDetail(id);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <p className="text-neutral-500">{t('common.loading')}</p>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex items-center justify-center py-20">
        <p className="text-neutral-500">{t('common.notFound')}</p>
      </div>
    );
  }

  return (
    <motion.div initial="hidden" animate="visible" variants={subtleNeo.fadeIn} className="space-y-6">
      {/* Breadcrumb */}
      <div className="flex flex-wrap items-center gap-2">
        <button
          onClick={() => navigate('/admin/users')}
          className="text-sm text-primary-600 hover:text-primary-700"
        >
          {t('users.title')}
        </button>
        <ArrowRight className="h-3.5 w-3.5 text-neutral-400 rtl:rotate-180" />
        <h1 className="text-lg sm:text-2xl font-semibold tracking-wide">
          {user.firstName} {user.lastName}
        </h1>
      </div>

      {/* User info card */}
      <div
        className="rounded-3xl overflow-hidden glass-surface p-6"
        style={{
          backgroundColor: ADMIN_COLORS.surface,
          border: `1px solid ${ADMIN_COLORS.border}`,
          boxShadow: ADMIN_COLORS.shadow,
        }}
      >
        <div className="flex flex-wrap items-start gap-6">
          {/* Avatar */}
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-100 text-2xl font-bold text-primary-600">
            {(user.firstName?.[0] || '').toUpperCase()}
            {(user.lastName?.[0] || '').toUpperCase()}
          </div>

          <div className="flex-1 space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-xl font-medium text-neutral-800">{user.firstName} {user.lastName}</h2>
              <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${ROLE_COLORS[user.role]}`}>
                {t(`users.roles.${user.role}`)}
              </span>
              <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${
                user.status === 'ACTIVE' ? 'bg-success-50 text-success-700' : 'bg-neutral-100 text-neutral-500'
              }`}>
                {t(`users.statuses.${user.status}`)}
              </span>
            </div>

            <div className="flex flex-wrap gap-4 text-sm text-neutral-500">
              <span className="flex items-center gap-1.5">
                <Mail className="h-3.5 w-3.5" /> {user.email}
              </span>
              {user.phone && (
                <span className="flex items-center gap-1.5">
                  <Phone className="h-3.5 w-3.5" /> {user.phone}
                </span>
              )}
              <span className="flex items-center gap-1.5">
                <Calendar className="h-3.5 w-3.5" /> {new Date(user.createdAt).toLocaleDateString()}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div
          className="p-4 rounded-xl glass-surface"
          style={{
            backgroundColor: ADMIN_COLORS.surface,
            border: `1px solid ${ADMIN_COLORS.border}`,
            boxShadow: ADMIN_COLORS.shadow,
          }}
        >
          <div className="flex items-center gap-2 mb-2">
            <Calendar className="w-4 h-4" style={{ color: ADMIN_COLORS.navy + '80' }} />
            <p className="text-xs font-bold text-slate-500 uppercase">{t('users.joined')}</p>
          </div>
          <p className="text-2xl font-bold text-slate-800">{new Date(user.createdAt).toLocaleDateString()}</p>
        </div>
        <div
          className="p-4 rounded-xl glass-surface"
          style={{
            backgroundColor: ADMIN_COLORS.surface,
            border: `1px solid ${ADMIN_COLORS.border}`,
            boxShadow: ADMIN_COLORS.shadow,
          }}
        >
          <div className="flex items-center gap-2 mb-2">
            <Mail className="w-4 h-4" style={{ color: ADMIN_COLORS.navy + '80' }} />
            <p className="text-xs font-bold text-slate-500 uppercase">{t('auth.email')}</p>
          </div>
          <p className="text-sm font-bold text-slate-800 truncate">{user.email}</p>
        </div>
      </div>

      <div className="flex justify-end">
        <Button variant="secondary" onClick={() => navigate('/admin/users')}>
          {t('common.back')}
        </Button>
      </div>
    </motion.div>
  );
}
